import { type Feature, featureCollection, type LineString, type Point, point } from '@turf/helpers';
import type { SymbolLayerSpecification } from 'mapbox-gl';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { LineProperties } from '@alltrails/types/CustomRoute';
import { useAppSelector } from '../../../redux';
import { customRouteCutSegmentEndMarkerId, cutSnapshotPolylineId, defaultSymbolLayerLayoutProps } from '../../../utils/layers';
import endSvg from '../../../utils/svgDefinitions/endSvg';
import { PolylineSource } from '../../Polyline';
import getLineStrings from './getLineStrings';

export const END_MARKER_SOURCE_ID = 'cut-snapshot-end-marker-source';

const endMarkerLayout: SymbolLayerSpecification['layout'] = {
  ...defaultSymbolLayerLayoutProps,
  'icon-image': endSvg.name,
  'icon-allow-overlap': true
};

type CutSnapshotPolylineProps = {
  hideArrows?: boolean;
};

const CutSnapshotPolyline = ({ hideArrows }: CutSnapshotPolylineProps) => {
  const cutData = useAppSelector(state => state.customRoute.cutData);

  const endMarkerData: Feature<Point, object> | undefined = useMemo(() => {
    if (!cutData) {
      return undefined;
    }
    const target = cutData.targetLatLng;
    return point<object>([target[1], target[0]], {});
  }, [cutData]);

  const data = useMemo(() => {
    if (!cutData) {
      return undefined;
    }
    const { existingSegment, startIndex } = cutData;
    const features = getLineStrings(existingSegment).map(feature =>
      feature.properties.pathIndex === startIndex ? { ...feature, properties: { ...feature.properties, opacity: 0.5 } } : feature
    );
    return featureCollection<LineString, LineProperties>(features);
  }, [cutData]);

  if (!data || !endMarkerData) {
    return null;
  }

  return (
    <>
      <Source id={END_MARKER_SOURCE_ID} type="geojson" data={endMarkerData}>
        <Layer id={customRouteCutSegmentEndMarkerId} source={END_MARKER_SOURCE_ID} type="symbol" layout={endMarkerLayout} />
      </Source>
      <PolylineSource data={data} hideArrows={hideArrows} uniqueId={cutSnapshotPolylineId} />
    </>
  );
};

export default CutSnapshotPolyline;
